import { useState, useCallback, useEffect } from 'react';
import { Modal, Input, Select, Button } from '@nexora/design-system';
import { formatCurrency } from '../../shared/utils/format';
import {
  type Account,
  type JournalEntryInput,
} from './useAccounting';

interface LineFormRow {
  accountId: string;
  debit: string;
  credit: string;
}

interface EntryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  accounts: Account[];
  onSubmit: (input: JournalEntryInput) => Promise<void>;
}

function flattenAccounts(list: Account[]): { value: string; label: string }[] {
  const result: { value: string; label: string }[] = [];
  for (const a of list) {
    result.push({ value: a.id, label: `${a.code} - ${a.name}` });
    if (a.children) result.push(...flattenAccounts(a.children));
  }
  return result;
}

function LineRow({
  line,
  index,
  accounts,
  onChange,
  onRemove,
  canRemove,
}: {
  line: LineFormRow;
  index: number;
  accounts: { value: string; label: string }[];
  onChange: (index: number, field: keyof LineFormRow, value: string) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
      <div style={{ flex: 1, minWidth: '180px' }}>
        <Select
          label={index === 0 ? 'Conta' : undefined}
          value={line.accountId}
          onChange={(e) => onChange(index, 'accountId', e.target.value)}
        >
          <option value="">Selecione...</option>
          {accounts.map((a) => (
            <option key={a.value} value={a.value}>{a.label}</option>
          ))}
        </Select>
      </div>
      <div style={{ flex: '0 0 120px' }}>
        <Input
          label={index === 0 ? 'Débito' : undefined}
          type="number"
          step="0.01"
          min="0"
          value={line.debit}
          onChange={(e) => onChange(index, 'debit', e.target.value)}
        />
      </div>
      <div style={{ flex: '0 0 120px' }}>
        <Input
          label={index === 0 ? 'Crédito' : undefined}
          type="number"
          step="0.01"
          min="0"
          value={line.credit}
          onChange={(e) => onChange(index, 'credit', e.target.value)}
        />
      </div>
      {canRemove && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemove(index)}
          style={{ marginBottom: '2px', color: '#ba1a1a' }}
        >
          X
        </Button>
      )}
    </div>
  );
}

export function EntryFormModal({
  isOpen,
  onClose,
  accounts,
  onSubmit,
}: EntryFormModalProps): JSX.Element {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<LineFormRow[]>([{ accountId: '', debit: '', credit: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setDate(new Date().toISOString().split('T')[0]);
      setDescription('');
      setLines([{ accountId: '', debit: '', credit: '' }]);
      setError(null);
    }
  }, [isOpen]);

  const handleLineChange = useCallback((index: number, field: keyof LineFormRow, value: string) => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, [field]: value } : l)));
  }, []);

  const handleAddLine = useCallback(() => {
    setLines((prev) => [...prev, { accountId: '', debit: '', credit: '' }]);
  }, []);

  const handleRemoveLine = useCallback((index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const accountOptions = flattenAccounts(accounts);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!description.trim()) { setError('Descrição e obrigatória.'); return; }
    if (lines.length < 2) { setError('O lancamento deve ter pelo menos 2 linhas.'); return; }

    const parsedLines = lines.map((l) => ({
      accountId: l.accountId,
      debit: parseFloat(l.debit) || 0,
      credit: parseFloat(l.credit) || 0,
    }));

    const hasEmptyAccount = parsedLines.some((l) => !l.accountId);
    if (hasEmptyAccount) { setError('Todas as linhas devem ter uma conta selecionada.'); return; }

    const totalDebit = parsedLines.reduce((s, l) => s + l.debit, 0);
    const totalCredit = parsedLines.reduce((s, l) => s + l.credit, 0);

    if (totalDebit === 0 && totalCredit === 0) {
      setError('Pelo menos uma linha deve ter valor de debito ou credito.');
      return;
    }

    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      setError(`Total de debitos (${formatCurrency(totalDebit)}) deve ser igual ao total de creditos (${formatCurrency(totalCredit)}).`);
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit({ date: date ?? '', description: (description ?? '').trim(), lines: parsedLines });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar lancamento.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Novo Lançamento" size="lg">
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <div style={{ flex: '0 0 200px' }}>
              <Input label="Data" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
            </div>
          </div>
          <Input
            label="Descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            placeholder="Ex: Venda a vista"
          />

          <div style={{ borderTop: '1px solid #e0e3e5', paddingTop: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#191c1e' }}>
                Linhas do Lançamento
              </span>
              <Button variant="outline" size="sm" onClick={handleAddLine}>
                + Adicionar linha
              </Button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              {lines.map((line, i) => (
                <LineRow
                  key={i}
                  line={line}
                  index={i}
                  accounts={accountOptions}
                  onChange={handleLineChange}
                  onRemove={handleRemoveLine}
                  canRemove={lines.length > 1}
                />
              ))}
            </div>

            {lines.length >= 2 && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.8125rem', color: '#43474e', textAlign: 'right' }}>
                Total Debito: {formatCurrency(lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0))} |
                Total Credito: {formatCurrency(lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0))}
              </div>
            )}
          </div>

          {error && (
            <p role="alert" style={{ margin: 0, fontSize: '0.8125rem', color: '#ba1a1a' }}>
              {error}
            </p>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.75rem',
            marginTop: '1.5rem',
            paddingTop: '1rem',
            borderTop: '1px solid #e0e3e5',
          }}
        >
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={submitting}>
            Lancar
          </Button>
        </div>
      </form>
    </Modal>
  );
}